import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import {
  getActiveNarrationCollector,
  NarrationCollector,
  setActiveNarrationCollector,
} from './ga-narration-collection-context';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import { chartSummary } from './global-ai-client-tools';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import {
  emptyTrace,
  endTrace,
  finishStep,
  type ReasoningTrace,
  reopenTrace,
  startStep,
  titleStep,
} from './reasoning-trace';
import type { ChartDataDigest } from './turn-outcome-digest';
import {
  summarizeAppliedChartFilters,
  withChartScope,
} from './turn-outcome-digest';

/**
 * Grounded landing narration for RESOLVE-FIRST builds (offer_page_or_custom's
 * lazy build chip and create_custom_page's pre-generated chip).
 *
 * The legacy confirm-navigate-resume flow narrated the finished page through a
 * resumed turn: `create_custom_page` (BUILD-HERE) awaited the page's widget
 * digests and its tool result carried them, so the model's next text was a
 * GROUNDED summary of what actually landed. The resolve-first click seeds the
 * draft and navigates with NO resumed turn — the build got faster but the
 * narration vanished (observed on stage 2026-07-10).
 *
 * This restores it without resurrecting the on-page build: at click time the
 * stashed page carries an `onSeeded` callback; when `CustomDashRoute` consumes
 * the stash it reports back the MINTED widget ids (conversion mints fresh
 * uuids — only the consumer knows the ids the widgets actually mount with).
 * That callback, built here, then:
 *   1. shows a "Reading the page data" step in the working section,
 *   2. arms a `NarrationCollector` for those ids (the same collector the
 *      command-driven build paths use — widget data reporters publish to the
 *      module-level registry unconditionally),
 *   3. on completion, fires a SILENT narrate-only turn seeded with the
 *      collected digests under the same grounding contract as the
 *      create_custom_page result — the model's reply is the grounded landing
 *      narration.
 *
 * Staleness: the collector always finishes its trace step, but the narrate
 * turn is skipped when the user has already left the draft; a re-click
 * disposes the previous collector via `collectorRef`; and `startAssistantTurn`
 * itself no-ops while another turn is running.
 */
export type SeededBuildNarrationDeps = {
  newId: () => string;
  nowMs: () => number;
  appendTranscript: (message: GlobalAiTranscriptMessage) => void;
  updateReasoningTrace: (id: string, trace: ReasoningTrace) => void;
  startAssistantTurn: (
    prompt: string,
    options?: { silent?: boolean; suppressReportOffer?: boolean }
  ) => Promise<void>;
  /** Live pathname (via ref) — the callback fires up to 30s after the click. */
  getCurrentPathname: () => string;
  /** Dispose-previous slot owned by the drawer, mirroring the bridge's ref. */
  collectorRef: { current: NarrationCollector | null };
};

const READING_STEP_TITLE = 'Reading the page data';

/**
 * Same tier shape as the bridge's `armPageNarration`: majority settled →
 * narrate at 5s; anything settled → 12s; hard backstop 30s (matched to the
 * single-chart settle budget) so a cold page never narrates blind earlier
 * than it must, and a warm page narrates as soon as everything reports.
 */
const DEADLINE_TIERS = (expectedCount: number) => [
  { atMs: 5_000, minReported: Math.max(1, Math.ceil(expectedCount / 2)) },
  { atMs: 12_000, minReported: 1 },
];
const COLLECTION_TIMEOUT_MS = 30_000;

function buildNarrationSeed(params: {
  prompt: string;
  digests: ChartDataDigest[];
  expectedCount: number;
}): string {
  const base =
    'The user clicked the build option and the PRE-GENERATED dashboard is ALREADY on screen as a new draft page — the build is DONE. Do NOT navigate, create or update any page, render a chart, apply filters, or offer any button or report.';
  if (params.digests.length === 0) {
    return `[System: ${base} No chart data has settled yet — do not describe specific charts or claim the page covers any particular metric; give ONE short sentence saying the dashboard is ready and its data is loading, then end your turn.] ${params.prompt}`;
  }
  const summaries = params.digests.map(chartSummary).join('\n---\n');
  if (params.digests.length < params.expectedCount) {
    return `[System: ${base} The page has ${params.expectedCount} charts; data has settled for ${params.digests.length} of them so far (the rest are still loading):\n${summaries}\n\nGive a brief grounded summary (1–2 sentences) leading with the concrete figures above, referring to charts by their titles, and mention the remaining charts are still loading. Do NOT invent contents for charts not listed — never name their metrics or values. Frame it as a NEWLY BUILT dashboard, not an update. Then end your turn.] ${params.prompt}`;
  }
  return `[System: ${base} These are the ONLY charts on the page — narrate ONLY these:\n${summaries}\n\nGive a brief grounded summary (1–2 sentences) that refers to these charts by their titles and leads with the concrete figures above. Do NOT mention any metric, theme, or section that is not one of the charts listed. Frame it as a NEWLY BUILT dashboard, not an update. Then end your turn.] ${params.prompt}`;
}

/**
 * Build the `onSeeded` callback a resolve-first build stashes alongside its
 * generated page. Invoked (at most once, consume-once stash) by
 * `CustomDashRoute` with the freshly minted widget items after seeding the
 * draft config — i.e. right after landing, before the widgets' data settles.
 */
export function beginSeededBuildNarration(
  params: {
    /** The build's user prompt (the narrate turn's context). */
    prompt: string;
    /** Draft pathname the click navigated to (staleness guard). */
    targetPathname: string;
    /** Filters applied on the landing — stamped onto every digest. */
    filters?: TalentDiscoveryAiFilterSearchResponseV2;
    /**
     * The click's ENDED build trace (lazy path). When present, the reading
     * step REOPENS it and joins the same working block — one click, one
     * block, one total duration — instead of opening a second block.
     * Reopen-on-seed keeps the failure mode safe: a landing that never
     * happens leaves the properly ended build trace untouched. Absent
     * (pre-generated chip: its build ran inside the offering turn's own
     * trace), the reading step gets its own small block.
     */
    resumeTrace?: { traceId: string; trace: ReasoningTrace };
  },
  deps: SeededBuildNarrationDeps
): (seededItems: PositionedItem[]) => void {
  return (seededItems) => {
    // Text items have no data-provider and never emit a digest — exclude them
    // so the collector doesn't ride out the full timeout waiting for them
    // (same exclusion as the bridge's armPageNarration).
    const itemIds = seededItems
      .filter((item) => item.plot?.chartType !== 'text')
      .map((item) => item.id);
    if (itemIds.length === 0) {
      return;
    }
    const filtersApplied = summarizeAppliedChartFilters(params.filters);

    // The "working" line for the wait: a step that runs while the page's
    // widgets settle, collapsing back to "Worked for Xs" when the narration
    // turn takes over. Resumed into the click's build trace when available,
    // else a fresh single-step trace.
    const stepId = deps.newId();
    const traceId = params.resumeTrace?.traceId ?? deps.newId();
    let trace = params.resumeTrace
      ? reopenTrace(params.resumeTrace.trace)
      : emptyTrace(deps.nowMs());
    trace = startStep(trace, stepId);
    trace = titleStep(trace, stepId, READING_STEP_TITLE);
    if (!params.resumeTrace) {
      deps.appendTranscript({
        id: traceId,
        role: 'assistant',
        kind: 'reasoning_trace',
        trace,
      });
    }
    deps.updateReasoningTrace(traceId, trace);

    deps.collectorRef.current?.dispose();
    const collector = new NarrationCollector({
      expectedItemIds: itemIds,
      deadlineTiers: DEADLINE_TIERS(itemIds.length),
      timeoutMs: COLLECTION_TIMEOUT_MS,
      onComplete: (digests) => {
        if (deps.collectorRef.current === collector) {
          deps.collectorRef.current = null;
        }
        if (getActiveNarrationCollector() === collector) {
          setActiveNarrationCollector(null);
        }
        trace = finishStep(trace, stepId, 'done');
        trace = endTrace(trace, deps.nowMs());
        deps.updateReasoningTrace(traceId, trace);
        // The user left the draft before its data settled: narrating a page
        // they are no longer looking at is worse than silence. (Two drafts
        // can share a pathname, so re-clicks are handled by the collectorRef
        // dispose above, not by this guard.)
        if (
          !globalAssistantPathsMatch(
            deps.getCurrentPathname(),
            params.targetPathname
          )
        ) {
          return;
        }
        const scoped =
          filtersApplied.length > 0
            ? digests.map((digest) =>
                withChartScope(digest, { filtersApplied })
              )
            : digests;
        void deps.startAssistantTurn(
          buildNarrationSeed({
            prompt: params.prompt,
            digests: scoped,
            expectedCount: itemIds.length,
          }),
          // A matching report was already offered alongside the build option;
          // re-offering it after the landing narration is noise (same
          // reasoning as the legacy build-here resume).
          { silent: true, suppressReportOffer: true }
        );
      },
    });
    deps.collectorRef.current = collector;
    setActiveNarrationCollector(collector);
  };
}
